import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CheckCircle, AlertTriangle } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Reset link is missing or invalid');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-4" data-testid="reset-password-invalid">
        <Card className="bg-zinc-900 border-zinc-800 p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
          <h1 className="text-xl font-heading font-bold mb-2">Invalid reset link</h1>
          <p className="text-zinc-400 text-sm mb-6">This link is missing the required token. Please request a new password reset.</p>
          <Button onClick={() => navigate('/forgot-password')} className="btn-primary w-full" data-testid="request-new-link-button">
            Request new link
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors"
          data-testid="back-to-auth-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </button>

        <div className="text-center mb-8">
          <img src="/logo.png" alt="FutureXV" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-heading font-bold">{done ? 'Password updated' : 'Choose a new password'}</h1>
          <p className="text-zinc-400 text-sm mt-2">
            {done ? 'You can now sign in with your new password.' : 'Pick something secure — at least 8 characters.'}
          </p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 p-6">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-password-confirmation">
              <div className="w-14 h-14 mx-auto bg-primary-neon/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-primary-neon" />
              </div>
              <p className="text-zinc-200">Your password has been reset successfully.</p>
              <Button onClick={() => navigate('/auth')} className="btn-primary w-full" data-testid="go-to-login-button">
                Sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <Label htmlFor="new-password" className="text-zinc-300">New password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="new-password"
                    data-testid="new-password-input"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-zinc-950 border-zinc-800 text-white h-12"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-zinc-300">Confirm password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="confirm-password"
                    data-testid="confirm-password-input"
                    type="password"
                    placeholder="Repeat new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-zinc-950 border-zinc-800 text-white h-12"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="btn-primary w-full py-6"
                disabled={loading}
                data-testid="reset-submit-button"
              >
                {loading ? 'Updating...' : 'Update password'}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
